import {
  type GetPresentationsByDemoArgs,
  useGetDemoQuery,
  useLazyGetPresentationsByDemoQuery,
} from '@carboncopyco/data';
import { PlayIcon } from '@carboncopyco/icons';
import type { EnhancedPresentation } from '@carboncopyco/types';
import {
  Badge,
  Button,
  Container,
  SkeletonRowLoading,
  TextBase,
  TooltipBubble,
  UserBasicInfo,
} from '@carboncopyco/ui';
import { isDesktop } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type Col, FixedTable } from '../../../FixedTable';
import { GiveADemoModal } from '../../../PresentationModals';
import { Screenshot } from '../../../Screenshot';
import { useLinksPermissions } from '../../../utils/hooks/useLinksPermissions';
import { useControlledFetch } from '../../Controls/useControlledFetch';
import { CreatedOnTooltip } from '../../CreatedOnTooltip';
import { EmptyState } from '../../EmptyState';
import { TableRowWrapper } from '../../TableRowWrapper';
import { handleSinglePlural } from '../../utils';
import { useDemoPageProps } from '../DemoPageContext';
import { PresentationRowButtons } from './PresentationRowButtons';
import { PresentationsControls } from './PresentationsControls';

const getCols = (isDemoShareable: boolean): Col<EnhancedPresentation>[] => [
  {
    alignment: 'left',
    title: 'Opportunity',
    width: 220,
    maxContentWidth: 200,
    render: (presentation) => {
      return (
        <div className="flex items-center justify-between gap-3 w-full">
          <div className="w-14 h-9 shrink-0">
            <Screenshot
              image={presentation.image}
              alt="demo"
              rounded="sm"
            />
          </div>
          <div className="flex flex-col flex-grow overflow-hidden">
            <TooltipBubble
              disabled={presentation.forClient.length < 18}
              placement="top-start"
              title={presentation.forClient}
            >
              <div className="overflow-hidden truncate text-ds-natural-600">
                <TextBase
                  ellipsis
                  color="text-ds-natural-600"
                  label={presentation.forClient}
                />
              </div>
              <div className="flex gap-1">
                <Badge
                  label={`V${String(presentation.revisionId + 1)}`}
                  variant="grey"
                />
              </div>
            </TooltipBubble>
          </div>
        </div>
      );
    },
  },
  {
    title: 'Date',
    alignment: 'left',
    width: 120,
    maxContentWidth: 116,
    render: ({ createTime }) => <CreatedOnTooltip dateTime={createTime} />,
  },
  {
    alignment: 'left',
    title: 'Creator',
    width: 130,
    maxContentWidth: 120,
    render: ({ creator }) => (
      <div className="flex items-center">
        <UserBasicInfo
          size="small"
          user={creator}
          textColor="text-ds-natural-600"
          truncateWidth={110}
          tooltip={creator.name.length > 15}
        />
      </div>
    ),
  },
  {
    alignment: 'right',
    title: 'Presented',
    width: 85,
    render: ({ totalPresentationViews }) => (
      <TooltipBubble
        title={handleSinglePlural(totalPresentationViews, 'Presented')}
        placement="top"
      >
        <TextBase
          label={totalPresentationViews || '-'}
          color="text-ds-natural-600"
        />
      </TooltipBubble>
    ),
  },
  ...(isDemoShareable
    ? [
        {
          alignment: 'right' as 'left' | 'center' | 'right' | undefined,
          title: 'Shared',
          width: 85,
          render: ({ totalShares }) => (
            <TooltipBubble
              title={handleSinglePlural(totalShares, 'Shared')}
              placement="top"
            >
              <TextBase
                label={totalShares || '-'}
                color="text-ds-natural-600"
              />
            </TooltipBubble>
          ),
        },
      ]
    : []),
  {
    alignment: 'right',
    title: 'Visited',
    width: 85,
    render: ({ totalShareViews }) => (
      <TooltipBubble
        title={handleSinglePlural(totalShareViews, 'Visited')}
        placement="top"
      >
        <TextBase
          label={totalShareViews || '-'}
          color="text-ds-natural-600"
        />
      </TooltipBubble>
    ),
  },
  {
    alignment: 'right',
    title: '',
    width: 230,
    render: (presentation) => (
      <PresentationRowButtons presentation={presentation} />
    ),
  },
];

export const PresentationsTable = () => {
  const {
    rootPath,
    handleCreatePresentation,
    organizationId,
    demoId,
    goToDemoPreview,
    playPresentation,
    desktopDomain,
    workspaceId,
  } = useDemoPageProps();

  const { getIsDemoShareable } = useLinksPermissions();

  const { data: demo } = useGetDemoQuery({
    organizationId,
    workspaceId,
    id: demoId,
  });

  const [
    showGiveADemoModal,
    {
      setTrue: setTrueShowGiveADemoModal,
      setFalse: setFalseShowGiveADemoModal,
    },
  ] = useBoolean();
  const navigate = useNavigate();

  const {
    items: presentations,
    fetchNextControlledBatch,
    isLoading,
    isLoadingBatch,
    error,
  } = useControlledFetch<EnhancedPresentation, GetPresentationsByDemoArgs>({
    lazyHook: useLazyGetPresentationsByDemoQuery,
    extraEndpointArgs: {
      organizationId,
      workspaceId,
      demoId,
    },
    mapUrlStateToEndpointArgs: {
      sort: 'sort',
      creators: 'creators',
      companies: 'companies',
      query: 'query',
    },
  });

  const cols = useMemo(
    () => getCols(getIsDemoShareable(demo)),
    [demo, getIsDemoShareable]
  );

  if (!isLoading && !presentations?.length) {
    return (
      <>
        <EmptyState
          title="Demo has no active presentations"
          description="Once a demo presentation is created, you can track it from here"
          action={
            <Button
              link
              blue
              label="Give a demo"
              leftIcon={<PlayIcon />}
              onClick={() =>
                isDesktop()
                  ? handleCreatePresentation?.()
                  : setTrueShowGiveADemoModal()
              }
            />
          }
        />
        {showGiveADemoModal && (
          <GiveADemoModal
            workspaceId={workspaceId}
            onClose={setFalseShowGiveADemoModal}
            open={showGiveADemoModal}
            demoId={demoId}
            organizationId={organizationId}
            goToPresentationPage={playPresentation}
            goToDemoPresentations={() => {
              navigate(`${rootPath}/presentations`);
            }}
            goToDemoPreview={goToDemoPreview}
            desktopDomain={desktopDomain}
          />
        )}
      </>
    );
  }

  return (
    <>
      <PresentationsControls />
      <div className="py-5">
        <FixedTable<EnhancedPresentation>
          error={error}
          data={presentations ?? []}
          cols={cols}
          isLoading={isLoading}
          handleFetchNextBatch={fetchNextControlledBatch}
          isLoadingBatch={isLoadingBatch}
          sideSpacing
          infiniteListMaxHeight={600}
          rowWrapper={TableRowWrapper}
          loader={
            <Container>
              <div className="w-ds-lg-modal">
                <SkeletonRowLoading
                  medium
                  itemsCount={5}
                />
              </div>
            </Container>
          }
        />
      </div>
    </>
  );
};
